import React, { Component } from 'react'
import { View } from 'react-native'
import styled from 'styled-components/native'

import BarChart from '../../Atoms/BarChart'
import { Text } from '../../Atoms/Typography'
import Touchable from '../../Atoms/Touchable'
import TranslatedText from '../../Atoms/TranslatedText'

import ValueChanger from '../../Molecules/ValueChanger'
import PageHeader from '../../Molecules/PageHeader'

import { formatPrice1, mapNames } from '../../../lib/business-formatters'
import { thousandSeparator } from '../../../lib/formatters'

import { translate } from '../../../services/translate'

import theme from '../../../styles/theme'

const barChartHeight = 250

const Container = styled(View)`
  flex: 1;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: ${theme.colours.lightgrey};
`

const ChartsContainer = styled(View)`
  width: 100%;
  flex-direction: row;
  borderTopColor: ${theme.colours.grey};
  borderTopWidth: 1px;
  borderBottomColor: ${theme.colours.grey};
  borderBottomWidth: 1px;
  background-color: ${theme.colours.white};
`

const ChartContainer = styled(View)`
  flex: 1;
  width: 50%;
  align-items: center;
  justify-content: space-between;
  padding: ${2 * theme.gutterUnit}px;
  padding-bottom: ${3 * theme.gutterUnit}px;
`

const TableContainer = styled(View)`
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
`
const Row = styled(View)`
  width: 100%;
  height: 45px;
  flex-direction: row;
  background-color: ${props => props.active ? theme.colours.active : theme.colours.lightgrey};
  borderBottomColor: ${theme.colours.grey};
  borderBottomWidth: 1px;
`

const TableHeader = styled(Row)`
  height: 50px;
  background-color: ${theme.colours.lightgrey};
`

const TotalRow = styled(Row)`
  background-color: ${theme.colours.grey};
`

const Column = styled(View)`
  width: 25%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const FirstColumn = styled(Column)`
  width: 25%;
  justify-content: flex-start;
  padding-left: ${4 * theme.gutterUnit}px;
  borderLeftColor: ${props => props.active ? theme.colours.orange : 'transparent'};
  borderLeftWidth: ${theme.gutterUnit}px;
`

const IncomeContainer = styled(View)`
  width: 50%;
  align-items: flex-end;
  justify-content: center;
  margin-right: ${0.5 * theme.gutterUnit}px;
`

const PercentageContainer = styled(View)`
  width: 50%;
  align-items: flex-start;
  justify-content: center;
  margin-left: ${0.5 * theme.gutterUnit}px;
`

const estimateIncome = (price, volumeNow, volumePrev, type) => {
  const averageVolume = (volumeNow + volumePrev) / 2
  if (type === 'CREDIT_CARDS') {
    return price * averageVolume / 1000000
  }
  if (type === 'DEPOSITS') {
    return (price / 100 / 100) * averageVolume * -1
  }
  return (price / 100 / 100) * averageVolume
}

const calculatePercentage = (value, prevValue, type, numberOfDp = 2) => {
  let signedPrevValue = prevValue
  if (type === 'DEPOSITS') {
    if (signedPrevValue > 0) {
      signedPrevValue = prevValue * -1
    }
  }
  const percentage = (value - signedPrevValue) / prevValue * 100
  const result = `${percentage.toFixed(numberOfDp)}%`
  if (result === '-0.00%') {
    return '0.00%'
  }
  return result
}

const renderPercentage = (percentage, type) => {
  const isNegative = percentage.indexOf('-') !== -1

  const negativeColour = type === 'DEPOSITS' ? theme.colours.green : theme.colours.red
  const positiveColour = type === 'DEPOSITS' ? theme.colours.red : theme.colours.green
  return (
    <Text
      medium
      size='xs'
      colour={isNegative ? negativeColour : positiveColour}
    >
      {percentage}
    </Text>
  )
}

const getMinValueForYAxis = (sliderMin, values, buffer = 25) => {
  const minBarValue = Math.min(...values.map(v => v.value))
  let minVal = minBarValue
  if (sliderMin != null) {
    minVal = Math.min(minBarValue, sliderMin)
  }
  return Math.max(minVal - buffer, 0)
}

const formatVolume = (val, type) => {
  if (type === 'CREDIT_CARDS') {
    return thousandSeparator((val / 1000000).toFixed(3))
  }
  return thousandSeparator(parseInt(val))
}
export default class BusinessesPage2 extends Component {
  shouldComponentUpdate (nextProps) {
    if (!nextProps.active) {
      return false
    }
    if (nextProps.active && !this.props.active) {
      return true
    }
    if (nextProps.enableChanges !== this.props.enableChanges) {
      return true
    }
    if (nextProps.selectedRound !== this.props.selectedRound) {
      return true
    }
    if (nextProps.selectedBusiness !== this.props.selectedBusiness) {
      return true
    }
    if (nextProps.decisions !== this.props.decisions) {
      return true
    }
    if (nextProps.items !== this.props.items) {
      return true
    }
    return false
  }

  render () {
    const { items, selectedBusiness, onBusinessSelect, decisions, onChangeValue, selectedRound, enableChanges } = this.props
    const selectedItem = items.find(it => it.type === selectedBusiness)
    const priceData = selectedItem.history.map(h => ({
      label: h.year,
      value: h.price1
    })).concat({
      label: selectedRound,
      value: decisions[selectedBusiness].price1Now,
      active: true
    })
    const volumeData = selectedItem.history.map(h => ({
      label: h.year,
      value: h.volume
    })).concat({
      label: selectedRound,
      value: decisions[selectedBusiness].volumeNow,
      active: true
    })

    const estimatedIncomes = items.map(it => estimateIncome(decisions[it.type].price1Now, decisions[it.type].volumeNow, it.volumePrev, it.type))
    const totalEstimatedIncome = estimatedIncomes.reduce((result, val) => {
      result += val
      return result
    }, 0)
    const totalPreviousIncome = items.reduce((result, it) => {
      result += it.incomePrev
      return result
    }, 0)
    return (
      <Container>
        <PageHeader text={`${translate('businessDecisions.screen2.screenHeader', 'PRICE / VOLUME TRADE OFF').toUpperCase()}: ${selectedItem.name}`} />
        <ChartsContainer>
          <ChartContainer style={{ borderRightColor: theme.colours.grey, borderRightWidth: 1 }}>
            <TranslatedText medium size='l' uppercase value='businessDecisions.screen2.priceHeading' default='PRICE' />
            <BarChart
              height={barChartHeight}
              width={420}
              values={priceData}
              labelName='Year'
              formatValue={(val) => formatPrice1(val, selectedBusiness)}
              activeColour={theme.colours.orange}
              yMin={getMinValueForYAxis(selectedItem.slider1Min, priceData)}
            />
          </ChartContainer>
          <ChartContainer>
            <TranslatedText medium size='l' uppercase value='businessDecisions.screen2.volumeHeading' default='VOLUME' />
            <BarChart
              height={barChartHeight}
              width={420}
              values={volumeData}
              labelName='Year'
              formatValue={(val) => formatVolume(val, selectedBusiness)}
              activeColour={theme.colours.orange}
              yMin={getMinValueForYAxis(decisions[selectedBusiness].volumeNow * 0.9, volumeData)}
            />
          </ChartContainer>
        </ChartsContainer>
        <TableContainer>
          <TableHeader>
            <FirstColumn><TranslatedText medium size='s' uppercase value='businessDecisions.screen2.businessHeading' default='BUSINESS' /></FirstColumn>
            <Column><TranslatedText medium size='s' uppercase value='businessDecisions.screen2.priceHeading' default='PRICE' /></Column>
            <Column><Text medium size='s'>{`${translate('businessDecisions.screen2.volumeEstimate', 'VOLUME ESTIMATE').toUpperCase()} (${translate('general.denominationShort', 'm')})`}</Text></Column>
            <Column><Text medium size='s'>{`${translate('businessDecisions.screen2.estimateIncome', 'ESTIMATED INCOME').toUpperCase()} (${translate('general.denominationShort', 'm')})`}</Text></Column>
          </TableHeader>
          {items.map((it, index) => (
            <Touchable key={it.id} onPress={() => onBusinessSelect(it.type)} touchableStyle={{ width: '100%' }}>
              <Row key={it.id} active={selectedBusiness === it.type}>
                <FirstColumn active={selectedBusiness === it.type}><Text medium size='xs'>{mapNames(it.name).toUpperCase()}</Text></FirstColumn>
                <Column>
                  <ValueChanger
                    enableChanges={enableChanges}
                    value={decisions[it.type].price1Now}
                    min={it.slider1Min}
                    max={it.slider1Max}
                    formatValue={(val) => formatPrice1(val, it.type)}
                    onIncrement={(val) => {
                      if (it.type !== selectedBusiness) {
                        onBusinessSelect(it.type)
                      }
                      onChangeValue(it.type, 'price1Now', val + 1)
                    }}
                    onDecrement={(val) => {
                      if (it.type !== selectedBusiness) {
                        onBusinessSelect(it.type)
                      }
                      onChangeValue(it.type, 'price1Now', val - 1)
                    }}
                  />
                </Column>
                <Column>
                  <ValueChanger
                    enableChanges={enableChanges}
                    value={decisions[it.type].volumeNow}
                    min={0}
                    formatValue={(val) => formatVolume(val, it.type)}
                    onIncrement={(val) => {
                      if (it.type !== selectedBusiness) {
                        onBusinessSelect(it.type)
                      }
                      onChangeValue(it.type, 'volumeNow', val + (it.volumePrev * 0.01))
                    }}
                    onDecrement={(val) => {
                      if (it.type !== selectedBusiness) {
                        onBusinessSelect(it.type)
                      }
                      onChangeValue(it.type, 'volumeNow', val - (it.volumePrev * 0.01))
                    }}
                    percentageChange={() => renderPercentage(calculatePercentage(decisions[it.type].volumeNow, it.volumePrev, null, 1))}
                  />
                </Column>
                <Column>
                  <IncomeContainer>
                    <Text medium size='xs'>{thousandSeparator(parseInt(estimatedIncomes[index]))}</Text>
                  </IncomeContainer>
                  <PercentageContainer>
                    {renderPercentage(calculatePercentage(estimatedIncomes[index], it.incomePrev, it.type, 1), it.type)}
                  </PercentageContainer>
                </Column>
              </Row>
            </Touchable>
          ))}
          <TotalRow>
            <FirstColumn><Text bold size='xs'>TOTAL</Text></FirstColumn>
            <Column />
            <Column />
            <Column>
              <IncomeContainer>
                <Text medium size='xs'>{thousandSeparator(parseInt(totalEstimatedIncome))}</Text>
              </IncomeContainer>
              <PercentageContainer>
                {renderPercentage(calculatePercentage(totalEstimatedIncome, totalPreviousIncome, null, 1))}
              </PercentageContainer>
            </Column>
          </TotalRow>
        </TableContainer>
      </Container>
    )
  }
}
